// QA.A3 (#950) AC5 — read-only project sidebar card showing the tenant QA
// policy alongside this project's last QA session result.
//
// Fetch-on-mount only. Policy + last session do not auto-refresh. User
// refreshes page to see updates. Acceptable staleness window for a sidebar
// status display. (Matches the lazy fetch pattern of the sibling
// EscalationOverridesSidebarCard — no polling, no tab-focus refresh.)
//
// Policy is tenant-scoped; the last session is this project's most recent.
// When the per-project override follow-on (P2, #950 AC3 deferred) ships, the
// policy block extends to show the resolved (override-aware) policy.

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';

interface Props {
  projectId: string;
}

interface QAPolicyRow {
  requiredTestTypes:    string[];
  coverageThreshold:    number;
  blockDeployOnFailure: boolean;
}

interface QASessionRow {
  verdict:    'PASS' | 'CONDITIONAL' | 'FAIL' | null;
  passRate:   string | null;
  executedAt: string | null;
  status:     string;
}

const TEST_TYPE_LABELS: Record<string, string> = {
  unit:          'Unit',
  integration:   'Integration',
  e2e:           'E2E',
  performance:   'Performance',
  accessibility: 'Accessibility',
};

const VERDICT_COLORS: Record<string, { color: string; bg: string }> = {
  PASS:        { color: 'var(--success)', bg: 'rgba(34,197,94,0.1)' },
  CONDITIONAL: { color: 'var(--warning)', bg: 'rgba(245,158,11,0.1)' },
  FAIL:        { color: 'var(--error)',   bg: 'rgba(239,68,68,0.1)' },
};

const cardCls = 'rounded-xl border shadow-sm';
const cardStyle: React.CSSProperties = {
  background: 'var(--surface)', borderColor: 'var(--border)', padding: 'var(--space-5)',
};

const sectionLabel: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
  color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em',
  margin: '0 0 6px 0',
};

const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  fontSize: 12, marginBottom: 4,
};

export default function QAPolicyStatusCard({ projectId }: Props) {
  const { myTenant } = useMyTenant();
  const tenantId = myTenant?.tenantId;
  const isAdmin = isAdminRole(myTenant?.role);

  const [policy, setPolicy] = useState<QAPolicyRow | null>(null);
  const [session, setSession] = useState<QASessionRow | null>(null);
  const [loading, setLoading] = useState(true);
  const cancelledRef = useRef(false);

  // Fetch once both projectId and tenantId are known. `loading` inits to true,
  // so we never call setState synchronously in the effect body (which trips
  // react-hooks/set-state-in-effect); loading is only cleared in the async
  // callback. Waiting on tenantId also avoids a brief "no policy" flash before
  // useMyTenant resolves.
  useEffect(() => {
    cancelledRef.current = false;
    if (!projectId || !tenantId) return;

    const policyP = apiFetch<QAPolicyRow>(`/tenants/${tenantId}/qa-policy`).catch(() => null);
    const sessionP = apiFetch<{ sessions: QASessionRow[] }>(`/projects/${projectId}/qa-sessions`)
      .then(r => r.sessions[0] ?? null)
      .catch(() => null);

    void Promise.all([policyP, sessionP]).then(([p, s]) => {
      if (cancelledRef.current) return;
      setPolicy(p);
      setSession(s);
      setLoading(false);
    });

    return () => { cancelledRef.current = true; };
  }, [projectId, tenantId]);

  return (
    <div className={cardCls} style={cardStyle}>
      <p
        className="uppercase tracking-wider"
        style={{
          ...sectionLabel,
          borderLeft: '3px solid var(--primary)', paddingLeft: 8, marginBottom: 12,
        }}
      >
        QA Policy &amp; Last Session
      </p>

      {loading ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
      ) : (
        <>
          {/* Policy block */}
          <div style={{ marginBottom: 14 }}>
            <p style={sectionLabel}>Current QA Policy</p>
            <div style={rowStyle}>
              <span style={{ color: 'var(--text-secondary)' }}>Pass rate threshold</span>
              <span style={{ color: 'var(--text)', fontWeight: 600 }}>
                {(policy?.coverageThreshold ?? 70)}%
              </span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: 'var(--text-secondary)' }}>Block deploy on failure</span>
              <span style={{ color: 'var(--text)', fontWeight: 600 }}>
                {(policy?.blockDeployOnFailure ?? true) ? 'Yes' : 'No'}
              </span>
            </div>
            <div style={{ ...rowStyle, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Required tests</span>
              <span style={{ color: 'var(--text)', fontWeight: 600, textAlign: 'right', maxWidth: 160 }}>
                {(policy?.requiredTestTypes ?? ['unit'])
                  .map(t => TEST_TYPE_LABELS[t] ?? t)
                  .join(', ')}
              </span>
            </div>
            {!policy && (
              <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '4px 0 0', fontStyle: 'italic' }}>
                Using defaults — no policy configured yet.
              </p>
            )}
          </div>

          {/* Last session block */}
          <div>
            <p style={sectionLabel}>Last QA Session</p>
            {session && session.verdict ? (
              <>
                <div style={rowStyle}>
                  <span style={{ color: 'var(--text-secondary)' }}>Verdict</span>
                  <span
                    data-testid="qa-last-verdict"
                    style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                      color: VERDICT_COLORS[session.verdict]?.color ?? 'var(--text)',
                      background: VERDICT_COLORS[session.verdict]?.bg ?? 'transparent',
                    }}
                  >
                    {session.verdict}
                  </span>
                </div>
                {session.passRate != null && (
                  <div style={rowStyle}>
                    <span style={{ color: 'var(--text-secondary)' }}>Pass rate</span>
                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>{session.passRate}%</span>
                  </div>
                )}
                {session.executedAt && (
                  <div style={rowStyle}>
                    <span style={{ color: 'var(--text-secondary)' }}>Executed</span>
                    <span style={{ color: 'var(--text)' }}>
                      {new Date(session.executedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No QA session run yet.</p>
            )}
          </div>

          {isAdmin && (
            <Link
              to="/settings/qa-policy"
              style={{
                display: 'inline-block', marginTop: 12, fontSize: 12, fontWeight: 600,
                color: 'var(--primary)', textDecoration: 'none',
              }}
            >
              Configure →
            </Link>
          )}
        </>
      )}
    </div>
  );
}
